// Fixed KOHC oral-care FAQ content. Quick answer is always shown; the
// labelled sections sit behind expandable panels.
export const FAQS = [
  {
    id: "store-dentures",
    question: "What is the best way to store dentures?",
    quickAnswer:
      "Clean them thoroughly and store them safely in a clean, clearly labelled denture pot.",
    sections: [
      {
        label: "Why?",
        paragraphs: [
          "Dentures should normally be removed at night to give the mouth and gums a rest.",
          "Depending on the type of denture, they may need to be stored dry or in water. Follow the person's dental advice or the manufacturer's instructions where these are available.",
          "In a care setting, always make sure the denture pot is clearly labelled with the person's name to prevent dentures from becoming lost or mixed up.",
        ],
      },
    ],
  },
  {
    id: "no-teeth-or-dentures",
    question: "How should I clean someone's mouth if they have no teeth or dentures?",
    quickAnswer: "Check the mouth regularly and gently remove any food or debris.",
    sections: [
      {
        label: "Why?",
        paragraphs: [
          "Dental plaque sticks particularly to hard surfaces such as teeth and dentures. If someone has neither, you do not need to brush all of the soft tissues twice a day.",
          "However, the mouth should still be checked routinely and kept clean, moist and comfortable.",
          "Look around the gums, cheeks, tongue and roof of the mouth. Make sure no food or debris has been left behind, particularly before bed. If cleaning is needed, gently wipe around the mouth with damp gauze or an appropriate mouth-cleaning product.",
        ],
      },
      {
        label: "When should I get help?",
        help: true,
        paragraphs: [
          "Seek advice if you notice ulcers, swelling, bleeding, unusual red or white patches, persistent soreness or anything else that concerns you.",
        ],
      },
    ],
  },
  {
    id: "broken-teeth-or-roots",
    question: "How should I clean someone's mouth if they have broken teeth or roots?",
    quickAnswer: "Broken teeth and roots still need to be brushed.",
    sections: [
      {
        label: "Why?",
        paragraphs: [
          "Plaque sticks to hard surfaces, including broken teeth and remaining roots.",
          "Use a small, soft-headed toothbrush and fluoride toothpaste to gently clean the broken teeth or roots and around the gum line twice a day.",
          "Don't avoid an area simply because a tooth is broken.",
        ],
      },
      {
        label: "When should I get help?",
        help: true,
        paragraphs: [
          "Arrange a dental assessment if a broken tooth or root is painful, sharp, loose, swollen or causing repeated problems.",
        ],
      },
    ],
  },
  {
    id: "bleeding-gums",
    question: "What should I do if the gums bleed when I brush them?",
    quickAnswer: "Keep brushing gently – don't avoid the bleeding area.",
    sections: [
      {
        label: "Why?",
        paragraphs: [
          "Healthy gums generally should not bleed. Bleeding during brushing is commonly a sign of inflammation caused by plaque being left around the gum line.",
          "Continue to gently but thoroughly brush the teeth and along the gum margins twice a day.",
          "With effective daily plaque removal, you should usually notice the bleeding reducing as the gums become healthier.",
        ],
      },
      {
        label: "When should I get help?",
        help: true,
        paragraphs: [
          "Seek advice from a dentist or healthcare professional if the bleeding is heavy, unexplained or continues despite good oral hygiene.",
        ],
      },
    ],
  },
  {
    id: "unpleasant-smell",
    question:
      "I'm brushing the teeth, but there is still an unpleasant smell from the mouth. What should I do?",
    quickAnswer: "Check that you're cleaning the teeth, between the teeth and the tongue.",
    sections: [
      {
        label: "Why?",
        paragraphs: [
          "Bacteria and debris in the mouth are common causes of bad breath.",
          "Make sure you are brushing all surfaces of every tooth, particularly around the gum line.",
          "Clean between the teeth with an appropriate interdental aid, as bacteria and food debris can collect in areas that a toothbrush cannot easily reach.",
          "Check the tongue too. If it has a coating, gently clean it with a toothbrush or tongue cleaner.",
        ],
      },
      {
        label: "When should I get help?",
        help: true,
        paragraphs: [
          "If the unpleasant smell persists despite thorough daily mouth care, arrange a dental assessment or seek advice from an appropriate healthcare professional.",
        ],
      },
    ],
  },
  {
    id: "best-toothpaste",
    question: "What is the best toothpaste to use?",
    quickAnswer:
      "For most adults with natural teeth, use a fluoride toothpaste containing 1,350–1,500 ppm fluoride.",
    sections: [
      {
        label: "Why?",
        paragraphs: [
          'There isn\'t one particular brand of toothpaste that is "best". The important thing is that it contains an appropriate amount of fluoride and is a toothpaste the person is happy to use.',
          "Fluoride helps strengthen and protect the teeth against tooth decay.",
          "For someone with a dry or sensitive mouth, swallowing difficulties, or difficulty managing foam, consider a low-foaming or SLS-free toothpaste.",
          "Sodium Lauryl Sulphate (SLS) is an ingredient that makes many toothpastes foam and can contribute to dryness or irritation for some people.",
        ],
      },
      {
        label: "When should I get help?",
        help: true,
        paragraphs: [
          "People at particularly high risk of tooth decay may need a higher-fluoride toothpaste prescribed by a dentist.",
        ],
      },
    ],
  },
  {
    id: "glycerine-swabs",
    question: "Are glycerol or glycerine swabs recommended for end-of-life mouth care?",
    quickAnswer:
      "No. Lemon and glycerine swabs are not recommended for end-of-life mouth care.",
    sections: [
      {
        label: "Why?",
        paragraphs: [
          "They do not effectively remove dental plaque and can make a dry mouth worse.",
          "At the end of life, the priority is to keep the person's mouth clean, moist and comfortable.",
          "Use appropriate mouth-care products according to the person's individual needs and care plan. This may include a soft toothbrush, gentle mouth cleaning and suitable products to keep the mouth and lips moist.",
          "The Royal College of Nursing advises against the use of lemon and glycerine swabs for end-of-life mouth care.",
        ],
      },
    ],
  },
  {
    id: "foam-sponge-swabs",
    question: "Can we use foam sponge swabs for mouth care?",
    quickAnswer: "KOHC does not advocate the use of foam sponge swabs for mouth care.",
    sections: [
      {
        label: "Why?",
        paragraphs: [
          "Foam-headed oral swabs have been subject to a UK Medical Device Alert because the foam head can detach from the stick, creating a potential choking hazard.",
          "They are banned in Wales, and many organisations elsewhere in the UK have also stopped using them.",
          "Importantly, foam swabs do not effectively remove dental plaque.",
          "Use an appropriate alternative, such as a soft toothbrush or purpose-designed mouth-cleaning product, according to the person's individual needs and your organisation's local policy.",
        ],
      },
    ],
  },
  {
    id: "spit-dont-rinse",
    question: "Should residents rinse their mouths after brushing their teeth?",
    quickAnswer: 'Encourage them to "spit, don\'t rinse".',
    sections: [
      {
        label: "Why?",
        paragraphs: [
          "After brushing, encourage the person to spit out the excess toothpaste but not rinse immediately with water.",
          "This leaves fluoride from the toothpaste on the teeth for longer.",
          "Fluoride helps strengthen the tooth surface and protect against tooth decay, so rinsing it away immediately reduces this benefit.",
          "Remember: spit, don't rinse.",
        ],
      },
    ],
  },
  {
    id: "swallowing-difficulties",
    question:
      "What should I do if someone has swallowing difficulties? I'm worried they may choke on the toothpaste.",
    quickAnswer:
      "Mouth care is still very important, but it may need to be adapted for someone with swallowing difficulties.",
    sections: [
      {
        label: "What should I do?",
        paragraphs: [
          "Position the person appropriately, usually upright with their head well supported, and always follow their individual swallowing and mouth-care plan.",
          "Use a small-headed toothbrush with only a small smear of fluoride toothpaste. A low-foaming or SLS-free toothpaste may be helpful because there is less foam for the person to manage.",
          "Avoid adding unnecessary water to the toothbrush and regularly remove excess toothpaste, saliva and debris from the mouth as you clean.",
          "Where clinically appropriate and staff have been trained to use one, a suction toothbrush may be recommended.",
        ],
      },
      {
        label: "Why is mouth care so important?",
        paragraphs: [
          "Don't avoid mouth care because someone has swallowing difficulties.",
          "People with swallowing difficulties can be at increased risk of aspirating bacteria and secretions from the mouth into their airway. Keeping the mouth as clean as possible is therefore particularly important.",
        ],
      },
      {
        label: "When should I get help?",
        help: true,
        paragraphs: [
          "If you're unsure how to provide mouth care safely, check the person's care plan and seek advice from their Speech and Language Therapist (SLT), dental professional or healthcare team.",
        ],
      },
    ],
  },
];
